'use strict';

import {createHtmlElement, lockScreenOrientation, rgbaToHexA, userPrevent} from '../../../functions';
import gsap from 'gsap';
import {changeLangToCurrent, getCurrentLang} from "../langSwitcher/langSwitcher";
import { isMobile, projectMode } from '../../../settings';
import {Switch} from "../../interfaces/switchers/Switch";
import {lordIconColors, setLordIcon} from "../../../lordicon";
import {Rain} from "./Rain";
import {Snow} from "./Snow";
import {ClearSky} from "./ClearSky";
import {Clouds} from "./Clouds";
import { Time } from '../../projectTime/Time';
import { OrbitDayAndNight } from '../../liveAnimations/DayNight/OrbitDayAndNight';
import { CloudsShader } from './CloudsShader';
import {BaseElement} from "../../interfaces/BaseElement";
import { Thunderstorm } from './Thunderstorm';
import { Drizzle } from './Drizzle';
import { Cookie } from '../../Cookie/Cookie';
import { DayNightButton } from '../../threejs/DayNightButton/DayNightButton';
// import { dynamicFogToggle } from '../../post-processing/Passes/Fog';

export class Weather extends Time {
    #element = null;
    #parent = null;
    #displayOptionsOptions = null;
    #animationDuration = 0.6;
    #iconUrl = 'https://openweathermap.org/img/wn/';
    #weatherIconColors = {
        primaryColor: null,
        secondaryColor: null,
        activeColor: null,
    };
    #elementsHeights = {
        states: 0,
        widgetSwitcher: 0,
    }
    #timeElement = null;

    static #currentWeather = null;
    static #active = config.features.weather?.active;
    static #weatherData = null;
    static #currentWeatherActive = config.features.weather?.currentWeather;
    static #currentWeatherSwitcher = null;
    static #defaultWeather = '';
    static #sunny = null;
    static #selectedWeather = null;
    static currentId = null;
    static effects = {};
    static #settings = config.features.weather?.settings;
    static #showed = true;
    static scope = null;

    constructor(parent, additionalClass = '',response) {
        super(response);

        this.#parent = parent;
        this.response = response;
        this.#displayOptionsOptions = isMobile ? document.querySelector('.pop-up-page.display-options-page').querySelector('.display-options__options') : this.#parent.querySelector('.display-options__options');
        this.#create(additionalClass);
        Weather.scope = this;
    }

    async #create(additionalClass) {
        Weather.#weatherData = this.response;
        this.#getIconColors();
        Weather.#defaultWeather = Weather.#weatherData.weather[0].main.toLowerCase();
        if (config.features.weather?.widget) {
            this.#element = createHtmlElement(this.#parent, '', 'div', ['weather-widget-wrapp', additionalClass]);
            this.#parent.insertBefore(this.#element, this.#parent.firstChild);
            this.#createWeatherWidget();
            this.#createWeatherWidgetToggler();
            this.#applyWidgetVisibility();
        }

        if (config.features.weather?.active)
            this.#createWeatherStates();
    }

    #createWeatherStates() {
        const weatherStatesWrapper = createHtmlElement(this.#displayOptionsOptions, '', 'div', ['display-options__weather-states', 'display-option'])
        const currentWeatherToggle = createHtmlElement(weatherStatesWrapper,'','div',['weather-states__current-weather-container']);
        const currentWeatherToggleText = createHtmlElement(currentWeatherToggle,changeLangToCurrent('Current weather'),'span',['weather-state__toggle-text','language-string']);
        currentWeatherToggleText.setAttribute('data-dictionary', 'Current weather');

        const headerText = createHtmlElement(weatherStatesWrapper, changeLangToCurrent('Viewing weather options'), 'span', ['weather-states__header', 'language-string']);
        headerText.setAttribute('data-dictionary', 'Viewing weather options');
        const weatherStatesButtons = createHtmlElement(weatherStatesWrapper, '', 'div', ['weather-states__buttons']);
        weatherStatesButtons.addEventListener('click', this.weatherHandler.bind(this));

        if (isMobile) {
            weatherStatesWrapper.style['display'] = 'none';
        }

        Weather.currentWeatherSwitcher = new Switch(currentWeatherToggle);
        Weather.currentWeatherSwitcher.checked = Weather.currentWeatherActive;
        Weather.currentWeatherSwitcher.listener = this.#currentWeatherSwitcherHandler.bind(this);

        Weather.settings.items.forEach(item => {
            const snowElement = createHtmlElement(weatherStatesButtons, '', 'label', ['weather-state', item]);
            snowElement.addEventListener('mouseover', (event) => {
                event.target.setAttribute('colors',`primary:${this.#weatherIconColors.activeColor},secondary:${this.#weatherIconColors.activeColor}`);
            });
            snowElement.addEventListener('mouseout', (event) => {
                if (event.target.getAttribute('isActiveWeather') === 'true') return;
                const customColorId = event.target.getAttribute('custom-color-id');
                if (customColorId) {
                    event.target.setAttribute('colors',`primary:${lordIconColors.customColors[customColorId].primaryColor},secondary:${lordIconColors.customColors[customColorId].secondaryColor}`);
                } else {
                    event.target.setAttribute('colors',`primary:${this.#weatherIconColors.primaryColor},secondary:${this.#weatherIconColors.secondaryColor}`);
                }
            });
            const iconParameters = {trigger:'hover',delay:'0'};
            if (document.body.classList.contains('light-theme')) {
                iconParameters.id = 'weather-state';
                iconParameters.primaryColor = '#fff';
                iconParameters.secondaryColor = '#fff';
            }
            setLordIcon(item, snowElement, iconParameters);
            const textElement = createHtmlElement(snowElement, changeLangToCurrent(item), 'span', ['weather-state__text', 'language-string']);
            textElement.setAttribute('data-dictionary', item);

            this.item = createHtmlElement(snowElement, '', 'input', []);
            this.item.type = 'checkbox';
            this.item.id = item;

            switch (item) {
                case 'rain':
                    Weather.effects.rain = new Rain();
                    break;
                case 'snow':
                    Weather.effects.snow = new Snow();
                    break;
                case 'clear':
                    Weather.effects.clear = new ClearSky();
                    Weather.sunny = this.item;
                    break;
                case 'drizzle':
                    Weather.effects.drizzle = new Drizzle();
                    break;
                case 'clouds':
                    this.clouds360 = new Clouds(Weather.weatherData);
                    this.cloudsOSM = new CloudsShader(Weather.weatherData);

                    if (OrbitDayAndNight.isActiveAndSelected) {
                        Weather.effects.clouds = this.cloudsOSM;
                    } else {
                        Weather.effects.clouds = this.clouds360;
                    }
                    
                    if (projectMode.AirMode.active && !projectMode.AirMode.hasSkyMask()) {
                        snowElement.classList.add('hidden');
                    }
                    
                    break;
                case 'thunderstorm':
                    Weather.effects.thunderstorm = new Thunderstorm();
                    break;
            }

            if(item === Weather.defaultWeather){
                Weather.currentWeather = this.item;
                if (Weather.currentWeatherActive) {
                    Weather.currentWeather.checked = true;
                }
            }
        });

        const event = new Event('change');
        Weather.currentWeatherSwitcher.checkbox.dispatchEvent(event);
        Weather.displayOptionsElementsTotalHeight += parseFloat(getComputedStyle(weatherStatesWrapper).height);
    }

    changeClouds(type) {
        let active = false;
        if (Weather.effects.clouds.active) {
            active = true;
            Weather.effects.clouds.disable();
        }

        if (type === 'osm') {
            Weather.effects.clouds = this.cloudsOSM;
        } else {
            Weather.effects.clouds = this.clouds360;
        }

        if (active) {
            Weather.effects.clouds.enable();
        }
    }

    #currentWeatherSwitcherHandler(e) {
        const weatherStateButtons = document.querySelector('.weather-states__buttons');

        switch (e.target.checked) {
            case true:
                weatherStateButtons && weatherStateButtons.classList.add('disabled');

                if (Weather.currentWeather) {
                    this.weatherHandler({ target: Weather.currentWeather });
                } else {
                    if (Weather.sunny) {
                        this.weatherHandler({ target: Weather.sunny });
                    } else if (Weather.selectedWeather) {
                        Weather.effects[Weather.selectedWeather.id].disable();
                        Weather.selectedWeather.checked = false;
                        Weather.selectedWeather = null;
                        this.#playDownAllLordIcons();
                    }
                }

                break;
            case false:

                if (e instanceof Event) {
                    this.weatherHandler({ target: Weather.sunny });
                    weatherStateButtons && weatherStateButtons.classList.remove('disabled');
                } else {
                    weatherStateButtons && weatherStateButtons.classList.remove('disabled');
                }

                break;
        }
    }

    weatherHandler(e) {
        if (!e?.target?.id)
            return;

        const currentId = e.target.id;
        Weather.currentId = currentId;
        const icon = e.target.parentElement.querySelector('.lord__icon');
        if (e instanceof Event) {
            Weather.currentWeatherSwitcher.checked = false;
            this.#currentWeatherSwitcherHandler({target: { checked: false}});

            if (Weather.selectedWeather) {
                Weather.effects[Weather.selectedWeather.id].disable();

                if (Weather.selectedWeather !== e.target) {
                    Weather.selectedWeather.checked = false;
                    Weather.selectedWeather = null;
                }
            }

            if (e.target.checked) {
                this.#playDownAllLordIcons();
                this.#highlightWeatherIcon(icon);
                Weather.effects[currentId].enable();
                Weather.selectedWeather = e.target;
            } else {
                const customColorId = icon.getAttribute('custom-color-id');
                if (customColorId) {
                    icon.setAttribute('colors',`primary:${lordIconColors.customColors[customColorId].primaryColor},secondary:${lordIconColors.customColors[customColorId].secondaryColor}`);
                } else {
                    icon.setAttribute('colors',`primary:${this.#weatherIconColors.primaryColor},secondary:${this.#weatherIconColors.secondaryColor}`);
                }
                icon.setAttribute('isActiveWeather', false);
                Weather.effects[currentId].disable();
                Weather.selectedWeather = null;
            }
        } else {
            if (Weather.selectedWeather) {
                Weather.effects[Weather.selectedWeather.id].disable();
                Weather.selectedWeather.checked = false;
                Weather.selectedWeather = null;
            }

            this.#playDownAllLordIcons();
            this.#highlightWeatherIcon(icon);
            Weather.effects[currentId].enable();
            Weather.selectedWeather = e.target;
        }

        if (!Weather.selectedWeather) {
            this.weatherHandler({ target: Weather.sunny });
        }

        lockScreenOrientation();
    }

    #playDownAllLordIcons() {
        const weatherStatesWrapper = document.querySelector('.weather-states__buttons');
        const weatherIcons = weatherStatesWrapper.getElementsByClassName('lord__icon');
        for (let i = 0; i < weatherIcons.length; i++) {
            const customColorId = weatherIcons[i].getAttribute('custom-color-id');
            if (customColorId) {
                weatherIcons[i].setAttribute('colors',`primary:${lordIconColors.customColors[customColorId].primaryColor},secondary:${lordIconColors.customColors[customColorId].secondaryColor}`);
            } else {
                weatherIcons[i].setAttribute('colors',`primary:${this.#weatherIconColors.primaryColor},secondary:${this.#weatherIconColors.secondaryColor}`);
            }
            // weatherIcons[i].setAttribute('colors',`primary:${this.#weatherIconColors.primaryColor},secondary:${this.#weatherIconColors.secondaryColor}`);
            weatherIcons[i].setAttribute('isActiveWeather', false);
        }

        const currentActiveText = weatherStatesWrapper.querySelector('.weather-state__text.active');
        currentActiveText && currentActiveText.classList.remove('active');
    }

    #highlightWeatherIcon(icon) {
        icon.setAttribute('colors',`primary:${this.#weatherIconColors.activeColor},secondary:${this.#weatherIconColors.activeColor}`);
        icon.setAttribute('isActiveWeather', true);
        const parent = icon.closest('.weather-state');
        const text = parent.querySelector('.weather-state__text');
        text && text.classList.add('active');
    }

    #getIconColors() {
        // const {primary,secondary} = config.features.icons.color;
        //
        // const primaryRgba = config.style.colors.find(color => color.key === primary) || { value: '255,255,255,1' };
        // const secondaryRgba = config.style.colors.find(color => color.key === secondary) || { value: '255,255,255,1' };
        // const activeRgba = config.style.colors.find(color => color.key === 'brand2');

        // this.#weatherIconColors.primaryColor = rgbaToHexA(primaryRgba.value.split(','),false);
        // this.#weatherIconColors.secondaryColor = rgbaToHexA(secondaryRgba.value.split(','),false);
        // this.#weatherIconColors.activeColor = rgbaToHexA(activeRgba.value.split(','), false);
        this.#weatherIconColors.primaryColor = lordIconColors.primary;
        this.#weatherIconColors.secondaryColor = lordIconColors.secondary;
        this.#weatherIconColors.activeColor = lordIconColors.active;
    }

    #createWeatherWidgetToggler() {
        if (isMobile)
            return;

        const weatherWidgetToggler = createHtmlElement(this.#displayOptionsOptions, '', 'div', ['display-options__weather-widget-toggler', 'display-option']);
        const weatherWidgetText = createHtmlElement(weatherWidgetToggler, changeLangToCurrent('Display weather widget'), 'span', ['weather-widget-toggler__text', 'language-string']);
        weatherWidgetText.setAttribute('data-dictionary', 'Display weather widget');
        const switcher = new Switch(weatherWidgetToggler);
        switcher.checked = !isMobile;
        switcher.listener = this.#weatherWidgetSwitcherHandler.bind(this);
        this.#elementsHeights.widgetSwitcher = parseFloat(getComputedStyle(weatherWidgetToggler).height);
        const event = new Event('change');
        switcher.checkbox.dispatchEvent(event);
    }

    #weatherWidgetSwitcherHandler(event) {
        if (userPrevent())
            return;

        let globalWeatherWidgetCookie = Cookie.getCookie('weatherWidget');
        if (!globalWeatherWidgetCookie) {
            globalWeatherWidgetCookie = {
                weatherWidget: 'hidden'
            };
        } else {
            globalWeatherWidgetCookie = JSON.parse(globalWeatherWidgetCookie);
        }

        const weatherWidgetElement = this.#element;

        if (isMobile)
            event.target.checked = true;

        if (event.target.checked) {
            weatherWidgetElement.classList.remove('hidden');
            gsap.fromTo(
              weatherWidgetElement,
              {opacity: 0},
              {opacity: 1, duration: this.#animationDuration,
                  onComplete: () => {
                      event.target.checked = true;
                      // userPrevent(false);
                  }});
            globalWeatherWidgetCookie.weatherWidget = 'expanded';
        } else {
            gsap.fromTo(
              weatherWidgetElement,
              {opacity: 1},
              {opacity: 0, duration: this.#animationDuration,
                  onComplete: () => {
                      event.target.checked = false;
                      weatherWidgetElement.classList.add('hidden');
                      // userPrevent(false);
                  }},
            );
            globalWeatherWidgetCookie.weatherWidget = 'hidden';
        }
        
        Cookie.setCookie('weatherWidget', JSON.stringify(globalWeatherWidgetCookie));
    }

    #createWeatherWidget() {
        const weatherWidget = new BaseElement()
          .addClass('weather-widget')
          .appendTo(this.#element);

        const currentTempWrapp = new BaseElement()
          .addClass('weather-temp-wrapp')
          .appendTo(weatherWidget.DOMElement);
        
        const currentTemp = new BaseElement()
          .addClass('weather-temp')
          .innerHtml(Weather.#weatherData.main.temp === null ? '---' : Math.round(Weather.#weatherData.main.temp) + '&#176;')
          .appendTo(currentTempWrapp.DOMElement);
        new BaseElement('img')
          .addClass('weather-icon')
          .addAttribute('src', this.#iconUrl + Weather.#weatherData.weather[0].icon + '.png')
          .appendTo(currentTemp.DOMElement);
        
          
        if (isMobile)
            return;
        
        new BaseElement()
          .addClass('weather-description', 'language-string')
          .addAttribute('data-dictionary', Weather.#weatherData.weather[0].description)
          .innerHtml(changeLangToCurrent(Weather.#weatherData.weather[0].description))
          .appendTo(currentTempWrapp.DOMElement);


        const descriptionWrapp = new BaseElement()
          .addClass('weather-description-wrapp')
          .appendTo(weatherWidget.DOMElement);
        
        const currentCityData = config.localization?.city;
        if (currentCityData && currentCityData.length > 0) {
            const currentCityElement = new BaseElement()
            .addClass('language-string', 'current-city')
            .appendTo(descriptionWrapp.DOMElement);

            for (const item of currentCityData) {
                currentCityElement.addAttribute(`data-lang-${item.lang}`, item.value);
                if (item.lang === getCurrentLang())
                    currentCityElement.innerHtml(item.value);
            }
        }
        const currentDate = new Date();
        currentDate.toLocaleString('default', { month: 'long' }).substr(0, 3);
        const dateWrapp = new BaseElement()
          .addClass('weather-date')
          .appendTo(descriptionWrapp.DOMElement);
        new BaseElement()
          .addClass('date','language-string')
          .addAttribute('data-dictionary', 'Local time')
          .innerHtml(changeLangToCurrent('Local time'))
          .appendTo(dateWrapp.DOMElement);
        this.#timeElement = new BaseElement()
          .addClass('time')
          .innerHtml(`${this.getCurrentTime}`)
          .appendTo(dateWrapp.DOMElement);
        const switchWrapp = new BaseElement()
          .addClass('switch-wrapp')
          .appendTo(descriptionWrapp.DOMElement);
          
        if (DayNightButton.canActivate())
            new DayNightButton(switchWrapp.DOMElement);
    }

    refreshTime() {
        if (this.#timeElement) {
            this.#timeElement.innerHTML = this.getCurrentTime;
        }
    }

    addClass(value) {
        this.#element.classList.add(value);
    }

    #applyWidgetVisibility() {
        const widgetCookies = Cookie.getCookie('weatherWidget');
        if (!widgetCookies)
            return;
        const widgetVisibilityParam = JSON.parse(widgetCookies).weatherWidget;
        if (widgetVisibilityParam === 'hidden' && !isMobile) {
            this.#element.classList.add('hidden');
            const displayOptions = this.#element.closest('.display-options');
            const weatherWidgetToggler = displayOptions.querySelector('.display-options__weather-widget-toggler');
            const widgetCheckboxParam = weatherWidgetToggler.querySelector('.switch__checkbox');
            widgetCheckboxParam.checked = false;
        }
    }

    static getWeatherData() {
        return Weather.#weatherData;
    }

    static render(time) {
        if (!Weather.#active) return;

        Weather.effects.snow?.update(time);
        Weather.effects.rain?.update(time);
        Weather.effects.clouds?.update(time);
        Weather.effects.thunderstorm?.update(time);
        Weather.effects.drizzle?.update(time);
    }

    resetToDefault() {
        Weather.currentWeatherSwitcher.checked = false;
        this.#currentWeatherSwitcherHandler({target: { checked: false}});
        this.weatherHandler({ target: Weather.sunny });
    }

    static isActive() {
        return Weather.#active;
    }

    static checkedCurrentWeather() {
        return Weather.#currentWeatherSwitcher.checkbox.checked;
    }

    static get showed() {
        return Weather.#showed;
    }

    static set showed(value) {
        Weather.#showed = value;
    }

    static get currentWeatherSwitcher() {
        return Weather.#currentWeatherSwitcher;
    }

    static set currentWeatherSwitcher(value) {
        Weather.#currentWeatherSwitcher = value;
    }

    static get currentWeatherActive() {
        return Weather.#currentWeatherActive;
    }

    static get sunny() {
        return Weather.#sunny;
    }

    static set sunny(value) {
        Weather.#sunny = value;
    }

    static get weatherData() {
        return Weather.#weatherData;
    }

    static get defaultWeather() {
        return Weather.#defaultWeather;
    }

    static get currentWeather() {
        return Weather.#currentWeather;
    }

    static set currentWeather(value) {
        Weather.#currentWeather = value;
    }

    static get selectedWeather() {
        return Weather.#selectedWeather;
    }

    static set selectedWeather(value) {
        Weather.#selectedWeather = value;
    }

    static get settings() {
        return Weather.#settings;
    }

    get DOMElement() {
        return this.#element;
    }
}


export function needsFogForSelectedWeather () {
    const selectedWeather = Weather.selectedWeather?.id;
    return selectedWeather === 'snow' || selectedWeather === 'rain';
}
